// In eine Funktion gekapselt: die Datei wird von mehreren Sections derselben
// Seite eingebunden. Eine Klassendeklaration auf oberster Ebene würfe ab dem
// zweiten Laden „Identifier has already been declared". customElements.define
// ist ohnehin geschützt; so bleibt auch die Klasse selbst lokal.
(() => {
/**
 * Loume — Audio-Karussell (S09)
 * =============================
 *
 * Ein Custom Element für den Streifen, gebaut wie <lo-gallery> in
 * loume-buy-box.js: Das Blättern erledigt CSS scroll-snap, das Skript hält
 * nur Punkte und Pfeile im Takt. Dazu <lo-audio> für die Player (unten).
 *
 * Grundsatz wie überall im Theme: ohne JavaScript bleibt alles benutzbar.
 * Der Streifen ist dann ein waagerecht scrollbarer Bereich, die Punkte sind
 * echte Sprunglinks (der Browser holt die Karte dann in den sichtbaren
 * Bereich, statt sie an den linken Rand zu rücken), und die Hörproben sind
 * native <audio>-Player mit allen Bedienelementen. Sichtbar wird durch dieses
 * Skript nichts — es blendet nur zwei Dinge ein bzw. aus, die ohne es
 * sinnlos wären: die Pfeile (können ohne Skript nicht scrollen) und die
 * Punkte, die auf keine erreichbare Position zeigen.
 *
 * Ein Punkt je Karte — so viele zeigt auch der bisherige Shop auf Desktop.
 * Der Streifen rastet am linken Rand ein; die letzten Karten können dort
 * nicht mehr ankommen, weil der Streifen vorher endet. Ein Klick auf einen
 * dieser Punkte fährt deshalb ans Ende, und der letzte Punkt leuchtet. Das
 * Original (keen-slider) verhält sich genauso, nur unsichtbar: es begrenzt
 * intern auf Index 3,22 von 6.
 */
class LoumeAudioCarousel extends HTMLElement {
  constructor() {
    super();
    // Erst hier — also mit garantiert laufendem Skript — dürfen die Pfeile
    // erscheinen. Genau dieselbe Absicherung wie bei <lo-reveal>.
    this.setAttribute('data-ready', '');
  }

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
    if (!this.slides.length) return;

    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (event) => {
        // Ohne das würde der Sprunglink die Adresszeile ändern und die Seite
        // senkrecht mitverschieben.
        event.preventDefault();
        this.goTo(index);
      });
    });

    if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));

    this.onChange = () => this.schedule();
    this.track.addEventListener('scroll', this.onChange, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onChange);
      this.resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', this.onChange);
    }

    this.update();
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.track && this.onChange) this.track.removeEventListener('scroll', this.onChange);
    if (this.onChange) window.removeEventListener('resize', this.onChange);
  }

  /** Scroll feuert sehr oft — einmal je Bild genügt. */
  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Weiteste Scrollposition des Streifens. */
  get maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  /**
   * Karte, die gerade am linken Rand steht.
   * Die Karten liegen ohne Zwischenschicht im Streifen, und der Streifen ist
   * `position: relative` — offsetLeft ist damit genau die Scrollposition,
   * bei der die Karte links anliegt.
   */
  leftmostIndex() {
    const left = this.track.scrollLeft;
    let index = 0;
    for (let i = 0; i < this.slides.length; i++) {
      if (this.slides[i].offsetLeft <= left + 1) index = i;
    }
    return index;
  }

  /**
   * Welcher Punkt leuchtet. Am Ende des Streifens der letzte — dort stehen
   * die letzten Karten nebeneinander im Blick, und „ganz rechts" ist die
   * Auskunft, die der Besucher erwartet.
   */
  activeIndex() {
    if (this.track.scrollLeft >= this.maxScroll - 1) return this.slides.length - 1;
    return this.leftmostIndex();
  }

  update() {
    const scrollable = this.maxScroll > 1;
    const active = this.activeIndex();

    this.dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', String(index === active));
    });

    // Pfeil verschwindet, sobald er nichts mehr bewirkt — im Original hieß
    // die Einstellung isHiddenArrowWhenDisabled.
    if (this.prevButton) this.prevButton.hidden = !scrollable || this.track.scrollLeft <= 1;
    if (this.nextButton) this.nextButton.hidden = !scrollable || this.track.scrollLeft >= this.maxScroll - 1;
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    this.track.scrollTo({
      left: Math.min(this.slides[clamped].offsetLeft, this.maxScroll),
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /**
   * Eine Karte weiter oder zurück. Bezugspunkt ist die Karte am linken Rand,
   * NICHT der leuchtende Punkt: am Streifenende zeigt der auf die letzte
   * Karte, und „zurück" bewegte dann nichts.
   */
  step(direction) {
    this.goTo(this.leftmostIndex() + direction);
  }
}

if (!customElements.get('lo-audio-carousel')) {
  customElements.define('lo-audio-carousel', LoumeAudioCarousel);
}

/**
 * <lo-audio> — Player der Hörproben
 * =================================
 *
 * Gründer, 27.09.2026: der Player soll auf allen Browsern und Systemen so
 * aussehen wie der von Chrome, ohne Download und Geschwindigkeit. Der native
 * Player sieht überall anders aus (Safari, Firefox, iOS), deshalb eine eigene
 * Pille: Abspielen · Zeit · Balken · Stumm. Aussehen in
 * loume-audio-carousel.css, Markup im Liquid.
 *
 * Ohne JavaScript bleibt das native <audio controls> stehen und die Pille
 * versteckt. Erst hier — mit laufendem Skript — tauschen beide.
 *
 * Verhalten:
 *   · Vor dem ersten Abspielen lädt nichts (preload="none", 0 Byte wie der
 *     native Player). Die Gesamtdauer steht bis dahin aus der Block-Einstellung
 *     „Länge der Hörprobe“ (data-dauer, „0:45“), sonst „0:00 / 0:00“ wie im
 *     Chrome-Player; mit den Kopfdaten gilt die echte Dauer. Früher holte der
 *     Player die Kopfdaten, sobald er in Sicht kam — Safari lud dabei die
 *     ganze Datei (gemessen 27.09.2026, bei den Firebase-Fassungen bis 43 MB).
 *   · Balken als Schieberegler (role="slider"): Klicken, Ziehen mit Maus und
 *     Finger (Pointer Capture — das Karussell scrollt dabei nicht mit),
 *     Pfeiltasten ±5 s, Bild auf/ab ±30 s, Pos1/Ende. Ein Sprung vor dem
 *     ersten Abspielen wird gemerkt und greift mit den Kopfdaten.
 *   · Senkrecht wischen, auch auf dem Balken, scrollt die Seite; der Balken
 *     springt dann nicht (Richtung entscheidet die erste Fingerbewegung).
 *   · Startet ein <audio> auf der Seite, halten alle anderen an — Hörproben
 *     untereinander und gegenüber der Sprachnachricht der Buy-Box.
 *   · Lädt die Datei nach dem Tippen noch, pulsiert der Knopf; ist sie nicht
 *     erreichbar, steht ein Hinweis in der Pille, und Abspielen versucht es
 *     erneut.
 */

/** Sekunden als m:ss, ab einer Stunde h:mm:ss. */
function zeitText(sekunden) {
  const s = Number.isFinite(sekunden) && sekunden > 0 ? Math.floor(sekunden) : 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

/** „0:45“, „12:40“, „1:02:03“ oder „45“ in Sekunden; sonst 0. */
function sekundenAus(text) {
  const t = String(text || '').trim();
  let m = /^(?:(\d+):)?(\d{1,2}):(\d{2})$/.exec(t);
  if (m) return Number(m[1] || 0) * 3600 + Number(m[2]) * 60 + Number(m[3]);
  m = /^(\d+)$/.exec(t);
  return m ? Number(m[1]) : 0;
}

const klemmen = (wert, min, max) => Math.min(max, Math.max(min, wert));

// Immer nur ein <audio> gleichzeitig. „play" steigt nicht auf, deshalb in der
// Capture-Phase am Dokument. Videos bleiben unberührt (stumme Loops).
if (!window.loumeNurEinAudio) {
  window.loumeNurEinAudio = true;
  document.addEventListener(
    'play',
    (event) => {
      const quelle = event.target;
      if (!(quelle instanceof HTMLAudioElement)) return;
      document.querySelectorAll('audio').forEach((audio) => {
        if (audio !== quelle && !audio.paused) audio.pause();
      });
    },
    true
  );
}

class LoumeAudio extends HTMLElement {
  connectedCallback() {
    if (this.verbunden) return;
    this.audio = this.querySelector('audio');
    this.pille = this.querySelector('[data-pille]');
    this.playKnopf = this.querySelector('[data-play]');
    this.tonKnopf = this.querySelector('[data-ton]');
    this.balken = this.querySelector('[data-balken]');
    this.zeitEl = this.querySelector('[data-zeit]');
    this.platzEl = this.querySelector('[data-zeit-platz]');
    this.fehlerEl = this.querySelector('[data-fehler]');
    if (!this.audio || !this.pille || !this.playKnopf || !this.balken) return;
    this.verbunden = true;

    this.texte = {
      play: this.dataset.labelPlay || 'Abspielen',
      pause: this.dataset.labelPause || 'Pausieren',
      zeit: this.dataset.labelZeit || '{aktuell} von {gesamt}',
      fehler: this.dataset.textFehler || '',
    };

    // Erst jetzt tauschen: natives Bedienfeld aus, Pille ein.
    this.audio.controls = false;
    this.pille.hidden = false;
    this.setAttribute('data-ready', '');

    this.ziehen = false;
    this.ziehAnteil = 0;
    this.wunsch = null; // Sprungziel, solange die Dauer noch fehlt
    this.vorgabe = sekundenAus(this.dataset.dauer); // Länge laut Einstellung
    this.wisch = null; // Fingergeste auf dem Balken

    this.playKnopf.addEventListener('click', () => this.umschalten());
    if (this.tonKnopf) {
      this.tonKnopf.addEventListener('click', () => {
        this.audio.muted = !this.audio.muted;
      });
    }

    const a = this.audio;
    const zeigen = () => this.anzeigen();
    ['loadedmetadata', 'durationchange', 'timeupdate', 'seeked', 'emptied'].forEach((typ) =>
      a.addEventListener(typ, zeigen)
    );
    a.addEventListener('loadedmetadata', () => {
      // Kopfdaten ohne jede Dauer sind nach Spezifikation unmöglich — dann
      // taugt die Datei nicht.
      if (Number.isNaN(a.duration)) {
        this.fehler();
        return;
      }
      this.wunschAnwenden();
    });
    a.addEventListener('play', () => {
      this.entferneFehler();
      this.setAttribute('data-spielt', '');
      this.playKnopf.setAttribute('aria-label', this.texte.pause);
      if (a.readyState < 3) this.setAttribute('data-laedt', '');
    });
    a.addEventListener('playing', () => {
      clearTimeout(this.ladeUhr);
      this.removeAttribute('data-laedt');
    });
    a.addEventListener('canplay', () => {
      if (a.paused) this.removeAttribute('data-laedt');
    });
    a.addEventListener('waiting', () => {
      if (!a.paused) this.setAttribute('data-laedt', '');
    });
    a.addEventListener('pause', () => this.angehalten());
    a.addEventListener('ended', () => this.angehalten());
    a.addEventListener('volumechange', () => this.tonAnzeigen());
    // Fehler einer <source> steigen nicht auf — in der Capture-Phase kommen
    // sie trotzdem hier vorbei.
    a.addEventListener('error', () => this.fehler(), true);

    this.balken.addEventListener('pointerdown', (e) => this.zeigerAb(e));
    this.balken.addEventListener('pointermove', (e) => this.zeigerZieht(e));
    this.balken.addEventListener('pointerup', (e) => this.zeigerAuf(e));
    this.balken.addEventListener('pointercancel', (e) => this.zeigerAbbruch(e));
    this.balken.addEventListener('lostpointercapture', (e) => this.zeigerAbbruch(e));
    this.balken.addEventListener('keydown', (e) => this.taste(e));
    // Fingergesten: touch-action: pan-y (CSS) lässt senkrecht die Seite
    // scrollen und meldet dann pointercancel. Für Browser, die touch-action
    // in einem waagerechten Scrollbereich nicht beachten, entscheidet hier
    // zusätzlich die erste Fingerbewegung: waagerecht zieht den Balken (das
    // Karussell bleibt stehen), senkrecht gibt die Geste an die Seite ab.
    // Früher blockierte der Handler jede Bewegung — die Seite ließ sich auf
    // dem Balken nicht scrollen, und beim Loslassen sprang die Hörprobe.
    this.balken.addEventListener('touchstart', (e) => this.wischAnfang(e), { passive: true });
    this.balken.addEventListener('touchmove', (e) => this.wischt(e), { passive: false });

    this.tonAnzeigen();
    this.anzeigen();
  }

  /* --- Laden ----------------------------------------------------------- */

  /**
   * Kopfdaten holen, nur auf Wunsch des Besuchers: Suchen vor dem ersten
   * Abspielen, wenn keine Länge eingestellt ist. Nie von selbst — Safari lädt
   * schon bei preload="metadata" die ganze Datei.
   */
  kopfdatenHolen() {
    const a = this.audio;
    if (a.readyState > 0 || a.networkState === 2 /* NETWORK_LOADING */) return;
    a.preload = 'metadata';
    // preload umzustellen genügt nicht jedem Browser; load() startet die
    // Quellenwahl sicher neu.
    if (a.paused) a.load();
  }

  /* --- Abspielen ------------------------------------------------------- */

  umschalten() {
    const a = this.audio;
    if (this.hasAttribute('data-fehler')) {
      // Neuer Versuch: Quelle frisch laden, dann abspielen.
      this.entferneFehler();
      a.load();
    } else if (!a.paused) {
      a.pause();
      return;
    }
    if (a.readyState < 3) this.setAttribute('data-laedt', '');
    // Kommt die Wiedergabe gar nicht in Gang, ohne dass der Browser einen
    // Fehler meldet, gilt sie nach 12 s als nicht erreichbar. So hängt Safari
    // bei einer Fehlerseite (403): Kopfdaten mit Dauer „unendlich", kein
    // error-Ereignis, ewig „lädt". Unendlich allein ist kein Fehler (Stream).
    clearTimeout(this.ladeUhr);
    this.ladeUhr = setTimeout(() => {
      if (this.hasAttribute('data-laedt')) this.fehler();
    }, 12000);
    const versprechen = a.play();
    if (versprechen && versprechen.catch) {
      versprechen.catch((err) => {
        const name = err && err.name;
        // AbortError: pause() oder load() kam dazwischen — kein Fehler.
        // NotAllowedError: Browser verweigert (Stromsparmodus u. ä.).
        if (name === 'AbortError' || name === 'NotAllowedError') {
          if (a.paused) this.removeAttribute('data-laedt');
          return;
        }
        this.fehler();
      });
    }
  }

  angehalten() {
    clearTimeout(this.ladeUhr);
    this.removeAttribute('data-spielt');
    this.removeAttribute('data-laedt');
    this.playKnopf.setAttribute('aria-label', this.texte.play);
    this.anzeigen();
  }

  /** Datei nicht erreichbar oder nicht abspielbar — schon beim Vorladen. */
  fehler() {
    clearTimeout(this.ladeUhr);
    // Ohne Quelle bleibt das Element sonst „spielend" (paused false) stehen,
    // und der nächste Klick hielte an, statt es neu zu versuchen.
    if (!this.audio.paused) this.audio.pause();
    this.removeAttribute('data-spielt');
    this.removeAttribute('data-laedt');
    this.playKnopf.setAttribute('aria-label', this.texte.play);
    this.setAttribute('data-fehler', '');
    if (this.fehlerEl) this.fehlerEl.textContent = this.texte.fehler;
  }

  entferneFehler() {
    if (!this.hasAttribute('data-fehler')) return;
    this.removeAttribute('data-fehler');
    if (this.fehlerEl) this.fehlerEl.textContent = '';
  }

  tonAnzeigen() {
    const stumm = this.audio.muted;
    this.toggleAttribute('data-stumm', stumm);
    if (this.tonKnopf) this.tonKnopf.setAttribute('aria-pressed', String(stumm));
  }

  /* --- Anzeige --------------------------------------------------------- */

  /** Echte Dauer aus den Kopfdaten, sonst 0. Maßgeblich fürs Springen. */
  dauer() {
    const d = this.audio.duration;
    return Number.isFinite(d) && d > 0 ? d : 0;
  }

  /** Was angezeigt wird: echte Dauer, bis dahin die eingestellte Länge. */
  anzeigeDauer() {
    return this.dauer() || this.vorgabe;
  }

  anzeigen() {
    const d = this.anzeigeDauer();
    let anteil;
    let t;
    if (this.ziehen) {
      anteil = this.ziehAnteil;
      t = anteil * d;
    } else if (!this.dauer() && this.wunsch !== null) {
      anteil = this.wunsch;
      t = anteil * d;
    } else {
      t = this.audio.currentTime || 0;
      anteil = d ? klemmen(t / d, 0, 1) : 0;
    }

    this.balken.style.setProperty('--lo-anteil', anteil.toFixed(5));

    const gesamt = zeitText(d);
    const aktuell = zeitText(t);
    const zeile = `${aktuell} / ${gesamt}`;
    if (this.zeitEl && this.zeitEl.textContent !== zeile) this.zeitEl.textContent = zeile;
    const platz = `${gesamt.replace(/\d/g, '0')} / ${gesamt}`;
    if (this.platzEl && this.platzEl.textContent !== platz) this.platzEl.textContent = platz;

    this.balken.setAttribute('aria-valuemax', String(Math.floor(d)));
    this.balken.setAttribute('aria-valuenow', String(Math.floor(t)));
    this.balken.setAttribute(
      'aria-valuetext',
      d ? this.texte.zeit.replace('{aktuell}', aktuell).replace('{gesamt}', gesamt) : aktuell
    );
  }

  /* --- Suchen ---------------------------------------------------------- */

  suchen(anteil) {
    const d = this.dauer();
    if (!d) {
      // Dauer noch unbekannt: Ziel merken, gesprungen wird mit den Kopfdaten
      // (spätestens beim Abspielen). Geladen wird dafür nur, wenn auch keine
      // Länge eingestellt ist — sonst wüsste die Anzeige nicht, wohin.
      this.wunsch = klemmen(anteil, 0, 1);
      if (!this.vorgabe) this.kopfdatenHolen();
      this.anzeigen();
      return;
    }
    this.wunsch = null;
    this.audio.currentTime = klemmen(anteil, 0, 1) * d;
    this.anzeigen();
  }

  wunschAnwenden() {
    if (this.wunsch === null) return;
    const anteil = this.wunsch;
    this.wunsch = null;
    this.suchen(anteil);
  }

  anteilAus(event) {
    const r = this.balken.getBoundingClientRect();
    const breite = r.width - 16; // Spur: 8px Rand auf jeder Seite
    return breite > 0 ? klemmen((event.clientX - r.left - 8) / breite, 0, 1) : 0;
  }

  zeigerAb(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // Keine Textauswahl, keine nachgeschobenen Mausereignisse. Den Fokus
    // setzen wir selbst, damit danach die Pfeiltasten greifen.
    event.preventDefault();
    this.balken.focus({ preventScroll: true });
    this.ziehen = true;
    this.zeigerId = event.pointerId;
    try {
      this.balken.setPointerCapture(event.pointerId);
    } catch (_) {
      /* ältere Browser: Ziehen endet dann am Balkenrand */
    }
    this.setAttribute('data-ziehen', '');
    this.ziehAnteil = this.anteilAus(event);
    this.anzeigen();
  }

  zeigerZieht(event) {
    if (!this.ziehen || event.pointerId !== this.zeigerId) return;
    this.ziehAnteil = this.anteilAus(event);
    this.anzeigen();
  }

  zeigerAuf(event) {
    if (!this.ziehen || event.pointerId !== this.zeigerId) return;
    this.ziehAnteil = this.anteilAus(event);
    this.zeigerEnde();
    this.suchen(this.ziehAnteil);
  }

  /** Browser hat die Geste übernommen (Seite scrollt) — Sprung verwerfen. */
  zeigerAbbruch(event) {
    if (!this.ziehen || event.pointerId !== this.zeigerId) return;
    this.zeigerEnde();
    this.anzeigen();
  }

  zeigerEnde() {
    this.ziehen = false;
    this.zeigerId = null;
    this.removeAttribute('data-ziehen');
  }

  wischAnfang(event) {
    const t = event.touches[0];
    this.wisch = t && event.touches.length === 1 ? { x: t.clientX, y: t.clientY, richtung: null } : null;
  }

  /**
   * Die erste Fingerbewegung entscheidet (danach ließe sich das Scrollen in
   * Safari nicht mehr aufhalten): überwiegend waagerecht → Balken ziehen und
   * das Karussell festhalten; sonst → Ziehen abbrechen (kein Sprung beim
   * Loslassen), Zeiger freigeben, die Seite scrollt.
   */
  wischt(event) {
    const w = this.wisch;
    const t = event.touches[0];
    if (!w || !t || event.touches.length !== 1) return;
    if (!w.richtung) {
      const dx = Math.abs(t.clientX - w.x);
      const dy = Math.abs(t.clientY - w.y);
      if (!dx && !dy) return;
      w.richtung = dx > dy ? 'waagerecht' : 'senkrecht';
      if (w.richtung === 'senkrecht' && this.ziehen) {
        const id = this.zeigerId;
        this.zeigerEnde();
        this.anzeigen();
        try {
          if (this.balken.hasPointerCapture(id)) this.balken.releasePointerCapture(id);
        } catch (_) {
          /* Zeiger schon weg */
        }
      }
    }
    if (w.richtung === 'waagerecht' && this.ziehen && event.cancelable) event.preventDefault();
  }

  taste(event) {
    // Vor den Kopfdaten zählt die eingestellte Länge und ein gemerkter Sprung.
    const d = this.anzeigeDauer();
    const t = !this.dauer() && this.wunsch !== null ? this.wunsch * d : this.audio.currentTime || 0;
    let ziel;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        ziel = t + 5;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        ziel = t - 5;
        break;
      case 'PageUp':
        ziel = t + 30;
        break;
      case 'PageDown':
        ziel = t - 30;
        break;
      case 'Home':
        ziel = 0;
        break;
      case 'End':
        ziel = d;
        break;
      default:
        return;
    }
    // Auch ohne Dauer abfangen: sonst scrollte das Karussell mit den Pfeilen.
    event.preventDefault();
    if (!d) {
      this.kopfdatenHolen();
      return;
    }
    this.suchen(ziel / d);
  }
}

if (!customElements.get('lo-audio')) {
  customElements.define('lo-audio', LoumeAudio);
}
})();
